import React, { useState, useEffect, useContext } from "react";
import axios from "../../api/axiosConfig";
import AuthContext from "../../util/AuthContext";
import { useNotification } from "../../util/NotificationContext";
import { TrashIcon, PlusIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns"; // Import date-fns for formatting

export default function ChefNotes({ recipeId }) {
  const { user, login } = useContext(AuthContext);
  const { addNotification } = useNotification();
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");

  useEffect(() => {
    if (user) {
      fetchNotes();
    }
  }, [user]);

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`/api/chef-notes/${recipeId}`, { withCredentials: true });
      setNotes(response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))); // Sort by newest first
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    try {
      const response = await axios.post(
        `/api/chef-notes`,
        { recipeId, note: newNote },
        { withCredentials: true }
      );
      setNotes([{ ...response.data, createdAt: new Date().toISOString() }, ...notes]); // Add new note to state
      setNewNote("");
      addNotification("Note added", "Your note has been saved.", "success");
    } catch (error) {
      console.error("Error adding note:", error);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`/api/chef-notes/${noteId}`, { withCredentials: true });
      setNotes(notes.filter((note) => note.id !== noteId));
      addNotification("Note deleted", "Your note has been removed.", "success");
    } catch (error) {
      console.error("Error deleting note:", error);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // Prevents new line in textarea
      handleAddNote();
    }
  };

  if (!user) {
    return (
      <div className="mt-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Chef’s notes</h3>
        <p className="text-gray-500 text-sm">
          <button onClick={login} className="text-green-600 underline">
            Log in
          </button>{" "}
          to add or view your own notes.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <h3 className="text-xl font-semibold text-gray-800 mb-2">Chef’s notes</h3>

      {/* Input Section with Right-Aligned "+" Button */}
      <div className="flex items-center space-x-2">
        <textarea
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add a cooking note..."
          className="w-full p-2 text-sm bg-white border border-gray-300 rounded-lg resize-none outline-none h-10"
        />
        <button
          onClick={handleAddNote}
          className="bg-green-600 text-white w-12 h-10 flex items-center justify-center rounded-md hover:bg-green-700 transition"
        >
          <PlusIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Notes Section - All Notes Inside a Single Box */}
      {notes.length > 0 && (
        <div className="bg-white border border-gray-300 rounded-lg p-4 mt-4">
          <ul className="space-y-3">
            {notes.map((note) => (
              <li key={note.id} className="flex justify-between items-center border-b last:border-0 pb-2 last:pb-0">
                <div>
                  <span className="text-sm text-gray-700">{note.note}</span>
                  <p className="text-xs text-gray-500 mt-1">
                    {format(new Date(note.createdAt), "dd/MM/yy")}
                  </p>
                </div>
                <button onClick={() => handleDeleteNote(note.id)} className="text-gray-500 hover:text-red-600 transition">
                  <TrashIcon className="w-5 h-5" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
